/**
 * useCommandPalette
 * Global command palette state + fuzzy search across all data contexts.
 */
import { useState, useMemo } from 'react';
import { useEstimates } from '../context/EstimateContext';
import { useInvoices } from '../context/InvoiceContext';
import { useCustomers } from '../context/CustomerContext';
import { useWorkOrders } from '../context/WorkOrderContext';

// ── Fuzzy subsequence scorer ──────────────────────────────────────────────────
// Returns a score > 0 if all query chars appear in text (in order), else 0.
// Higher score = better match (consecutive runs score higher).
function fuzzyScore(text, query) {
  if (!query) return 1;
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  let ti = 0;
  let qi = 0;
  let score = 0;
  let consecutive = 0;
  while (ti < t.length && qi < q.length) {
    if (t[ti] === q[qi]) {
      consecutive++;
      score += consecutive; // reward runs
      qi++;
    } else {
      consecutive = 0;
    }
    ti++;
  }
  return qi === q.length ? score : 0;
}

// ── Static help shortcuts ─────────────────────────────────────────────────────
const STATIC_SHORTCUTS = [
  {
    id: 'nav-dashboard',
    type: 'shortcut',
    title: 'Go to Dashboard',
    subtitle: 'Main overview',
    icon: 'squares-2x2',
    view: 'dashboard',
  },
  {
    id: 'nav-estimates',
    type: 'shortcut',
    title: 'Go to Estimates',
    subtitle: 'All estimates',
    icon: 'document-text',
    view: 'estimates',
  },
  {
    id: 'nav-invoices',
    type: 'shortcut',
    title: 'Go to Invoices',
    subtitle: 'All invoices',
    icon: 'banknotes',
    view: 'invoices',
  },
  {
    id: 'nav-settings',
    type: 'shortcut',
    title: 'Go to Settings',
    subtitle: 'App settings',
    icon: 'wrench-screwdriver',
    view: 'settings',
  },
];

// ── Max results per type ──────────────────────────────────────────────────────
const MAX_PER_TYPE = 2; // 4 types × 2 = 8 total

export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { estimates = [] } = useEstimates();
  const { invoices = [] } = useInvoices();
  const { customers = [] } = useCustomers();
  const { workOrders = [] } = useWorkOrders();

  const results = useMemo(() => {
    const q = query.trim();

    // ── Static shortcuts (always shown when empty, or fuzzy-matched) ──────────
    const shortcutResults = STATIC_SHORTCUTS
      .map((s) => {
        const score = fuzzyScore(s.title, q);
        return { ...s, score };
      })
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((s) => ({
        id: s.id,
        type: 'shortcut',
        title: s.title,
        subtitle: s.subtitle,
        icon: s.icon,
        onSelect: (navigate) => navigate(s.view),
      }));

    if (!q) {
      return shortcutResults;
    }

    // ── Estimate results ──────────────────────────────────────────────────────
    const estimateResults = estimates
      .map((e) => {
        const searchStr = [e.estimateNumber, e.customerName, e.vehicleLabel, e.package].filter(Boolean).join(' ');
        const score = fuzzyScore(searchStr, q);
        return { e, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_PER_TYPE)
      .map(({ e }) => ({
        id: `estimate-${e.id}`,
        type: 'estimate',
        title: e.estimateNumber || 'Estimate',
        subtitle: [e.customerName, e.vehicleLabel].filter(Boolean).join(' — '),
        icon: 'document-text',
        onSelect: (navigate) => navigate('estimates', { initialId: e.id }),
      }));

    // ── Invoice results ───────────────────────────────────────────────────────
    const invoiceResults = invoices
      .map((inv) => {
        const searchStr = [inv.invoiceNumber, inv.customerName, inv.vehicleLabel].filter(Boolean).join(' ');
        const score = fuzzyScore(searchStr, q);
        return { inv, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_PER_TYPE)
      .map(({ inv }) => ({
        id: `invoice-${inv.id}`,
        type: 'invoice',
        title: inv.invoiceNumber || 'Invoice',
        subtitle: [inv.customerName, inv.vehicleLabel].filter(Boolean).join(' — '),
        icon: 'banknotes',
        onSelect: (navigate) => navigate('invoices', { initialId: inv.id }),
      }));

    // ── Customer results ──────────────────────────────────────────────────────
    const customerResults = customers
      .map((c) => {
        const searchStr = [c.name, c.email, c.phone].filter(Boolean).join(' ');
        const score = fuzzyScore(searchStr, q);
        return { c, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_PER_TYPE)
      .map(({ c }) => ({
        id: `customer-${c.id}`,
        type: 'customer',
        title: c.name || 'Customer',
        subtitle: c.email || c.phone || '',
        icon: 'user-circle',
        onSelect: (navigate) => navigate('customers'),
      }));

    // ── Work order results ────────────────────────────────────────────────────
    const workOrderResults = workOrders
      .map((wo) => {
        const searchStr = [wo.title, wo.customerName, wo.vehicleLabel].filter(Boolean).join(' ');
        const score = fuzzyScore(searchStr, q);
        return { wo, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_PER_TYPE)
      .map(({ wo }) => ({
        id: `workorder-${wo.id}`,
        type: 'workorder',
        title: wo.title || 'Work Order',
        subtitle: [wo.customerName, wo.vehicleLabel].filter(Boolean).join(' — '),
        icon: 'wrench',
        onSelect: (navigate) => navigate('orders'),
      }));

    return [
      ...shortcutResults,
      ...estimateResults,
      ...invoiceResults,
      ...customerResults,
      ...workOrderResults,
    ];
  }, [query, estimates, invoices, customers, workOrders]);

  return { open, setOpen, query, setQuery, results };
}
